const hasOwn = (source, key) =>
  Boolean(
    source && typeof source === 'object' && Object.prototype.hasOwnProperty.call(source, key),
  );

export const MESSENGER_V2_VIEW_FIELDS = Object.freeze([
  'attachmentComposer',
  'attachmentMenuOpen',
  'attachmentViewer',
  'callParticipantsDialog',
  'chatStatusMarquee',
  'composerLinkPreview',
  'composerDrafts',
  'contactsDirectoryOpen',
  'conversationNavigation',
  'deleteDialog',
  'desktopNotificationPermission',
  'editingMessageId',
  'emojiPicker',
  'emojiUsage',
  'expandedMessageBodyIds',
  'groupDialog',
  'groupDirectory',
  'groupExitDialog',
  'groupManagement',
  'headerMenuOpen',
  'highlightedMessageId',
  'infoActionDialog',
  'infoActionPhase',
  'mobileSelectedConversation',
  'mobileSelectedMessageId',
  'openMessageMenuId',
  'openRailMenuId',
  'presenceNow',
  'replyMessageContext',
  'replySwipe',
  'sidebarMode',
  'sidebarQuery',
  'videoCallExpanded',
  'voiceCallNow',
]);

const MESSENGER_V2_VIEW_FIELD_SET = new Set(MESSENGER_V2_VIEW_FIELDS);

export const createInitialMessengerV2ViewState = ({
  initialSidebarMode = 'direct',
  now = Date.now(),
} = {}) => ({
  attachmentComposer: {
    dragActive: false,
    phase: 'idle',
    completed: 0,
    total: 0,
  },
  attachmentMenuOpen: false,
  attachmentViewer: null,
  callParticipantsDialog: null,
  chatStatusMarquee: { distance: 0, duration: 0 },
  composerLinkPreview: {
    preview: null,
    phase: 'idle',
    dismissedUrl: null,
  },
  composerDrafts: {},
  contactsDirectoryOpen: false,
  conversationNavigation: {
    lastDirectConversationId: null,
    lastGroupConversationId: null,
  },
  deleteDialog: null,
  desktopNotificationPermission: 'default',
  editingMessageId: null,
  emojiPicker: {
    open: false,
    source: null,
    targetMessageId: null,
    query: '',
    category: 'frequent',
    skinTone: 'default',
  },
  emojiUsage: {},
  expandedMessageBodyIds: {},
  groupDialog: {
    open: false,
    mode: 'create',
    phase: 'idle',
    error: null,
    groupId: null,
    name: '',
    description: '',
    selectedContactIds: [],
    initialView: 'members',
  },
  groupDirectory: {
    groupsDirectory: {
      phase: 'ready',
      items: [],
      error: null,
    },
    groupCategories: {
      phase: 'idle',
      items: [],
      error: null,
    },
    groupAvailableUsers: {
      groupId: null,
      phase: 'idle',
      items: [],
      error: null,
    },
  },
  groupExitDialog: null,
  groupManagement: {
    removingMemberIds: {},
    updatingMemberRoleIds: {},
    permissionsPhase: 'idle',
    adminEditorOpen: false,
    avatarPhase: 'idle',
    avatarCropItem: null,
    avatarCropOpen: false,
  },
  headerMenuOpen: false,
  highlightedMessageId: null,
  infoActionDialog: null,
  infoActionPhase: 'idle',
  mobileSelectedConversation: null,
  mobileSelectedMessageId: null,
  openMessageMenuId: null,
  openRailMenuId: null,
  presenceNow: now,
  replyMessageContext: null,
  replySwipe: null,
  sidebarMode: initialSidebarMode === 'groups' ? 'groups' : 'direct',
  sidebarQuery: '',
  videoCallExpanded: false,
  voiceCallNow: now,
});

const resolveNextValue = (currentValue, nextValue) =>
  typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;

export const reduceMessengerV2ViewState = (viewState, action) => {
  if (!viewState || typeof viewState !== 'object') {
    return createInitialMessengerV2ViewState();
  }

  switch (action?.type) {
    case 'view:field-updated': {
      const field = action.field;
      if (!MESSENGER_V2_VIEW_FIELD_SET.has(field) || !hasOwn(viewState, field)) {
        return viewState;
      }
      const nextValue = resolveNextValue(viewState[field], action.value);
      if (Object.is(nextValue, viewState[field])) {
        return viewState;
      }
      return {
        ...viewState,
        [field]: nextValue,
      };
    }
    case 'view:emoji-used': {
      const emoji = typeof action.emoji === 'string' ? action.emoji.trim() : '';
      if (!emoji) return viewState;
      const previousUsage = viewState.emojiUsage?.[emoji] || null;
      const nextUsage = {
        ...(viewState.emojiUsage || {}),
        [emoji]: {
          count: (Number(previousUsage?.count) || 0) + 1,
          lastUsedAt: Number(action.now) || Date.now(),
        },
      };
      return {
        ...viewState,
        emojiUsage: nextUsage,
        emojiPicker: action.closePicker
          ? {
              ...viewState.emojiPicker,
              open: false,
              source: null,
              targetMessageId: null,
              query: '',
            }
          : viewState.emojiPicker,
      };
    }
    case 'view:configured': {
      const nextSidebarMode = action.initialSidebarMode === 'groups' ? 'groups' : 'direct';
      const nextNavigation = {
        ...viewState.conversationNavigation,
        lastDirectConversationId:
          action.lastDirectConversationId ??
          viewState.conversationNavigation.lastDirectConversationId,
        lastGroupConversationId:
          action.lastGroupConversationId ??
          viewState.conversationNavigation.lastGroupConversationId,
      };
      const nextPermission =
        action.desktopNotificationPermission ?? viewState.desktopNotificationPermission;
      if (
        viewState.sidebarMode === nextSidebarMode &&
        viewState.desktopNotificationPermission === nextPermission &&
        viewState.conversationNavigation.lastDirectConversationId ===
          nextNavigation.lastDirectConversationId &&
        viewState.conversationNavigation.lastGroupConversationId ===
          nextNavigation.lastGroupConversationId
      ) {
        return viewState;
      }
      return {
        ...viewState,
        sidebarMode: nextSidebarMode,
        desktopNotificationPermission: nextPermission,
        conversationNavigation: nextNavigation,
      };
    }
    case 'view:reset':
      return createInitialMessengerV2ViewState({
        initialSidebarMode: action.initialSidebarMode ?? viewState.sidebarMode,
        now: action.now,
      });
    default:
      return viewState;
  }
};
